import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FileSearch, RefreshCw } from 'lucide-react';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Card, CardHead, INK } from '../abf/onboarding/review/reviewStyles';
import { Note, SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import { Table, money, words } from '../abf/board/boardShared';
import { agreementReviewPath } from '../../utils/loanNavigation';
import { getPcAgreement, pcErrorMessage } from './api';
import type { PcAgreement, PcCovenantDefinition } from './types';

/**
 * The Agreement workspace of a private credit facility (PC workflow parity, slice 4): the terms in
 * force as the record holds them, the covenants with what amended them, the amendment history from
 * both lanes (typed-in proposals and document amendments) and the agreement the facility was read
 * from. Read-only; amendments are proposed and approved where they always were.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.5;
  h2, h3, p { margin: 0; } h2 { font-size: 16px; color: ${INK.strong}; } h3 { font-size: 13px; color: ${INK.faint}; text-transform: uppercase; letter-spacing: .07em; font-weight: 800; }
  > * + * { margin-top: 14px; }
`;
const Groups = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 700px) { grid-template-columns: 1fr; }
  .group { border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised}; padding: 10px 12px; }
  .row { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; border-top: 1px solid ${INK.line}; font-size: 13px; }
  .row:first-of-type { border-top: 0; }
  .row span { color: ${INK.muted}; }
  .row strong { font-weight: 600; text-align: right; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
  .row strong.none { color: ${INK.faint}; font-weight: 500; }
`;

const show = (label: string, value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') return <strong className="none">not recorded</strong>;
  if (typeof value === 'boolean') return <strong>{value ? 'yes' : 'no'}</strong>;
  if (typeof value === 'number' && /commitment|funded|undrawn|capitalised|cap/i.test(label) && Math.abs(value) >= 1000) return <strong>{money(value)}</strong>;
  if (typeof value === 'string' && /^[A-Z_]+$/.test(value) && value.length > 2) return <strong>{words(value)}</strong>;
  return <strong>{String(value)}</strong>;
};
/** F-PCP-033: a threshold reads in its own unit — a ratio as 6.50x, an amount as money, a percentage as 12.5%. */
const MONEY_WORDS = /liquidity|capex|capital|ebitda|amount|net_worth|networth|cash|debt_service|usd|dollar/i;
const PCT_WORDS = /percent|pct|ltv|margin_pct|ratio_pct/i;
const thresholdWords = (c: { metric?: string | null; category?: string | null; type?: string | null; name?: string }, value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const n = Number(value); const key = `${c.metric || ''} ${c.category || ''} ${c.type || ''} ${c.name || ''}`;
  if (Math.abs(n) >= 10000 || MONEY_WORDS.test(key)) return money(n);
  if (PCT_WORDS.test(key)) return `${n.toFixed(2)}%`;
  return `${n.toFixed(2)}x`;
};
/** F-PCP-034: "DOCUMENT-INGESTION:First Amendment" is the lane's stamp; the reader wants the amendment's name and that it came from a document. */
/** F-PCP-056: a summary stored at booking time in the engine's own shorthand, read back in words
 *  ("spread ?→375bps | all-in ?→8.600000% | leverage 6.0000→6.5" → "spread not recorded → 375 bps · …"). */
const summaryWords = (text: string | null | undefined): string => {
  if (!text) return '';
  const two = (n: string) => Number(n).toFixed(2);
  return text
    .replace(/\s*\|\s*/g, ' · ')
    .replace(/\?\s*→/g, 'not recorded → ')
    .replace(/(\d+(?:\.\d+)?)\s*bps/g, (_m, n) => `${Number(n)} bps`)
    .replace(/(\d+\.\d{3,})%/g, (_m, n) => `${two(n)}%`)
    .replace(/(\d+\.\d{2,})\s*→\s*(\d+(?:\.\d+)?)(?![\d.]*\s*(?:%|bps))/g, (_m, a, b) => `${two(a)}x → ${two(b)}x`)
    .replace(/\s*→\s*/g, ' → ');
};
const amendedByWords = (by: string | null | undefined): string => {
  if (!by) return 'amended';
  const m = /^DOCUMENT-INGESTION:(.+)$/.exec(by);
  return m ? `${m[1]} (read from document)` : by;
};
const covenantTone = (c: PcCovenantDefinition): StatusTone => !c.active || c.retiredAt ? 'neutral' : c.amendedAt ? 'warning' : 'done';
const steps = (c: PcCovenantDefinition): string => {
  if (!c.stepSchedule) return '';
  try {
    const rows = JSON.parse(c.stepSchedule) as Array<{ date?: string; value?: number }>;
    return rows.map(r => `${r.value} from ${r.date}`).join(' · ');
  } catch { return c.stepSchedule; }
};

const AgreementWorkspace: React.FC<{ loanId: number; refreshKey?: number | string }> = ({ loanId, refreshKey }) => {
  const [data, setData] = useState<PcAgreement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await getPcAgreement(loanId)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The agreement could not be read.')); }
    finally { setBusy(false); }
  }, [loanId]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  if (!data) return <Note role={error ? 'alert' : 'status'}><p>{error || 'Reading the agreement…'}</p></Note>;
  const active = data.covenants.filter(c => c.active && !c.retiredAt);
  const retired = data.covenants.filter(c => !c.active || c.retiredAt);

  return <Shell aria-label="Agreement">
    <Card>
      <CardHead>
        <div><h2>Terms in force</h2><p>As the facility record holds them. A term the agreement does not state is shown as not recorded.</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          {data.sourceAgreement && <SmallButton as={Link} to={agreementReviewPath(data.sourceAgreement.jobId)}><FileSearch size={14} /> Agreement &amp; sources</SmallButton>}
          <SmallButton type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={14} /> Refresh</SmallButton>
        </div>
      </CardHead>
      {data.sourceAgreement && <Note style={{ marginBottom: 12 }} role="status"><p>Read from <Link to={agreementReviewPath(data.sourceAgreement.jobId)}>{data.sourceAgreement.documentName}</Link>{data.sourceAgreement.templateVersion ? ` with the Credit Agreement template v${data.sourceAgreement.templateVersion}` : ' with the built-in instructions'}{data.sourceAgreement.reviewedBy ? `, approved by ${data.sourceAgreement.reviewedBy}` : ''}; booking {words(data.sourceAgreement.bookingStatus || 'unknown')}. Every term there sits beside the passage it came from.</p></Note>}
      {!data.sourceAgreement && <Note style={{ marginBottom: 12 }}><p>No credit agreement document is linked to this facility: its terms were typed in.</p></Note>}
      <Groups>
        {Object.entries(data.terms).map(([group, rows]) => <div className="group" key={group}>
          <h3 style={{ marginBottom: 6 }}>{group}</h3>
          {rows.map(r => <div className="row" key={r.label}><span>{r.label}</span>{show(r.label, r.value)}</div>)}
        </div>)}
      </Groups>
    </Card>

    <Card>
      <CardHead><div><h2>Covenants</h2><p>{active.length} in force{retired.length ? `, ${retired.length} retired` : ''}. The threshold in force, what amended it, and the step schedule and cure terms the agreement states.</p></div></CardHead>
      <Table style={{ margin: 0 }}>
        <thead><tr><th>Covenant</th><th>Test</th><th className="num">Threshold</th><th>Amended</th><th>Steps · cure</th><th>Source</th></tr></thead>
        <tbody>
          {data.covenants.map(c => <tr key={c.id} style={{ opacity: !c.active || c.retiredAt ? .6 : 1 }}>
            <td><strong>{c.name}</strong><br /><small className="muted">{words(c.type || '')}{c.category ? ` · ${words(c.category)}` : ''}{c.metric ? ` · ${words(c.metric)}` : ''}</small></td>
            <td>{c.direction ? `${c.direction === 'MAX' ? 'at most' : c.direction === 'MIN' ? 'at least' : words(c.direction)}` : '—'}<br /><small className="muted">{[c.frequency, c.period].filter(Boolean).map(w => words(w as string)).join(' · ')}</small></td>
            <td className="num">{thresholdWords(c, c.threshold)}{c.thresholdMax != null ? ` – ${thresholdWords(c, c.thresholdMax)}` : ''}<br /><StatusChip tone={covenantTone(c)} label={!c.active || c.retiredAt ? 'retired' : c.amendedAt ? 'amended' : 'original'} /></td>
            <td>{c.amendedAt ? <>{c.previousThreshold != null ? `${thresholdWords(c, c.previousThreshold)} → ${thresholdWords(c, c.threshold)}` : 'amended'}<br /><small className="muted">{amendedByWords(c.amendedBy)} · {String(c.amendedAt).slice(0, 10)}</small></> : <span className="muted">—</span>}</td>
            <td><small>{steps(c) || (c.hasStepSchedule ? 'step schedule' : 'no steps')}{c.hasEquityCure ? ` · equity cure${c.maxEquityCures != null ? ` ×${c.maxEquityCures}` : ''}${c.equityCureCap != null ? ` up to ${money(c.equityCureCap)}` : ''}` : ''}{c.curePeriodDays != null ? ` · ${c.curePeriodDays}-day cure` : ''}</small></td>
            <td><small className="muted">{c.sourceClause || c.sourceRef || (data.sourceAgreement ? 'credit agreement' : 'typed in')}</small></td>
          </tr>)}
          {data.covenants.length === 0 && <tr><td colSpan={6} className="muted">No covenant is defined on this facility.</td></tr>}
        </tbody>
      </Table>
    </Card>

    <Card>
      <CardHead><div><h2>Amendments</h2><p>Every change to the agreement, newest first: proposals typed in and approved here, and amendments read from a document. Both lanes change the same record.</p></div></CardHead>
      <Table style={{ margin: 0 }}>
        <thead><tr><th>Amendment</th><th>Effective</th><th>Lane</th><th>State</th><th>What changed</th><th>Who</th></tr></thead>
        <tbody>
          {data.amendments.map(a => <tr key={`${a.kind}-${a.id}`}>
            <td><strong>{a.name || (a.kind === 'DOCUMENT' ? 'Document amendment' : 'Proposal')}</strong></td>
            <td>{a.effectiveDate || '—'}</td>
            <td>{a.kind === 'DOCUMENT' ? <>{a.jobId ? <Link to={agreementReviewPath(a.jobId)}>document</Link> : 'document'}{a.templateVersion ? <small className="muted"> · template v{a.templateVersion}</small> : null}</> : 'typed in'}</td>
            <td><StatusChip tone={['APPLIED', 'APPROVED'].includes(String(a.status)) ? 'done' : ['REJECTED', 'FAILED'].includes(String(a.status)) ? 'critical' : 'progress'} label={words(a.status || '')} /></td>
            <td><small>{a.kind === 'DOCUMENT' ? summaryWords(a.message) : [a.covenantUpdates && `covenants: ${a.covenantUpdates}`, a.proposedTerms && `terms: ${a.proposedTerms}`, a.notes].filter(Boolean).join(' · ')}{a.fee != null && Number(a.fee) ? ` · fee ${money(a.fee, data.currency)}` : ''}</small></td>
            <td><small className="muted">{a.kind === 'DOCUMENT' ? `read ${String(a.appliedAt || '').slice(0, 10)}${a.approvedBy ? ` · approved by ${a.approvedBy}` : ` · ${a.appliedBy || ''}`}` : `${a.requestedBy || ''} ${String(a.requestedAt || '').slice(0, 10)}${a.decidedBy ? ` → ${a.decidedBy} ${String(a.decidedAt || '').slice(0, 10)}` : ''}`}</small></td>
          </tr>)}
          {data.amendments.length === 0 && <tr><td colSpan={6} className="muted">No amendment recorded. Propose one from Underwriting, or import an amendment in Document Processing.</td></tr>}
        </tbody>
      </Table>
    </Card>
  </Shell>;
};

export default AgreementWorkspace;
